import * as React from 'react';
import { compose, setDisplayName, withState } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { State } from 'reducers';
import { getReport } from 'selectors/reportSelectors';
import { deleteReportRequest, editReport } from 'actions/reportActions';
import { ReportId, Report, SessionId } from 'types';
import { ReportsTable } from './ReportsTable';
import { NewReportDialog, FormValues } from 'ui/pages/reports/NewReportDialog';
import { Button } from 'ui/shared/Button';
import { getSessionId, getLanguage } from 'selectors/shellSelectors';
import { getReportsProtocol, getReportsPort } from 'selectors/serverSelectors';

import './ReportsPage.css';

export const RawReportsPage = ({
    canRun, canCopy, canEdit, canDelete,
    onAcceptNewReport, onCancelNewReport, onClickNew, onClickRun, onClickCopy, onClickEdit, onClickDelete,
    newDialogVisible, setSelectedReportId, selectedReportId, newDialogFields,
    styles, t,
}: MergedProps & OemProps & TranslateProps) =>
    <div id="reports-page" className="page" style={ styles.Workspace }>
        { newDialogFields !== null &&
            <NewReportDialog
                visible={ newDialogVisible }
                onAccept={ onAcceptNewReport }
                onCancel={ onCancelNewReport }
                reportId={ newDialogFields.reportId }
                initialName={ newDialogFields.name }
                initialReportType={ newDialogFields.reportType }
                initialShared={ newDialogFields.shared }
            />
        }
                
        <div className="rel">
            <h1>{ t('2730') }</h1>
            <Button
                id="new-report"
                onClick={ onClickNew }
                textKey="6.1241.0.8"
            />
        </div>
        <div
            className="reports-table"
            style={ styles.Bordered }
        >
            <ReportsTable
                selectedReportId={ selectedReportId }
                onSelect={ setSelectedReportId }
            />
        </div>
        <div className="button-bar">
            <Button onClick={ onClickRun } disabled={ !canRun } textKey="6.1241.0.14"/>
            <Button onClick={ onClickEdit } disabled={ !canEdit } textKey="292"/>
            <Button onClick={ onClickCopy } disabled={ !canCopy } textKey="2564"/>
            <span className="right">
                <Button onClick={ onClickDelete } disabled={ !canDelete } textKey="2948"/>
            </span>
        </div>
    </div>
;

type RecomposeProps = {
    sessionId: SessionId | null;
    reportsProtocol: 'http' | 'https';
    reportsPort: number;
    report: Report | null;
    language: string;
    selectedReportId: ReportId | null;
    setSelectedReportId: (reportId: ReportId | null) => void;
    newDialogVisible: boolean;
    newDialogFields: FormValues;
};
type _RecomposeProps = RecomposeProps & {
    _setNewDialogVisibility: (b: boolean) => void;
    _setNewDialogFields: (fields: FormValues) => void;
};
type StateProps = {
    canRun: boolean;
    canCopy: boolean;
    canEdit: boolean;
    canDelete: boolean;
    selectedReport: Report | null;
};
type DispProps = {
    onAcceptNewReport: (values: FormValues) => void;
    onCancelNewReport: () => void;
    onClickNew: () => void;
    onClickRun: () => void;
    onClickCopy: () => void;
    onClickEdit: () => void;
    onClickDelete: () => void;
};
type MergedProps = RecomposeProps & StateProps & DispProps & {
    onClickCopy: () => void;
    onClickEdit: () => void;
};

const mapStateToProps = (state: State, { selectedReportId }: RecomposeProps) => {
    const selectedReport = getReport(state, { reportId: selectedReportId });
    return {
        canRun: !!selectedReport, // TODO: does it make sense to run a report that is scheduled to collect data at a certain time?
        canCopy: !!selectedReport,
        canEdit: !!selectedReport,
        canDelete: !!selectedReport,
        selectedReport,
    };
};

const mapDispatchToProps = (dispatch: Function, { report, sessionId, reportsPort, reportsProtocol, selectedReportId, language, _setNewDialogFields, _setNewDialogVisibility, t }: _RecomposeProps & TranslateProps) => ({ // tslint:disable-line
    onAcceptNewReport: (values: FormValues) => {
        // Make a copy if the reportId field is set
        const copy = !!values.reportId;

        _setNewDialogVisibility(false);
        dispatch(editReport(values.reportId, { copy, values }));
    },
    onCancelNewReport: () => _setNewDialogVisibility(false),
    onClickNew: () => {
        _setNewDialogFields({
            reportId: null,
            name: '',
            reportType: 'AcrossLine',
            shared: false,
        });
        _setNewDialogVisibility(true);
    },
    onClickCopy: () => {
        _setNewDialogFields({
            reportId: selectedReportId,
            name: t('TextAndSubtext', [report!.name, t('2564')]),
            reportType: report!.reportType,
            shared: report!.shared,
        });
        _setNewDialogVisibility(true);
    },
    onClickEdit: () => dispatch(editReport(selectedReportId, { copy: false })),
    onClickDelete: () => dispatch(deleteReportRequest(selectedReportId!)),
    onClickRun: () => {
        const { hostname } = window.location;
        const timestamp = new Date().getTime();
        const reportUrl = `${reportsProtocol}://${hostname}:${reportsPort}/Reports/ReportRunner.aspx?K=RI${timestamp}&ID=${selectedReportId}&SE=${sessionId}&WC=1&L=${language}`; // tslint:disable-line
        const options = 'height=600, width=900, resizable=yes, scrollbars=yes, menubar=no, toolbar=no, location=no, status=no, directories=no';
        window.open(reportUrl, '_blank', options);
    },
});

export const ReportsPage = compose<MergedProps & OemProps & TranslateProps, {}>(
    setDisplayName('ReportsPage'),
    skin,
    translate(),
    withState('selectedReportId', 'setSelectedReportId', null),
    connect((state: State, { selectedReportId }: RecomposeProps) => ({
        sessionId: getSessionId(state),
        report: getReport(state, { reportId: selectedReportId }),
        reportsProtocol: getReportsProtocol(state),
        reportsPort: getReportsPort(state),
        language: getLanguage(state),
    })),
    withState('newDialogVisible', '_setNewDialogVisibility', false),
    withState('newDialogFields', '_setNewDialogFields', null),
    connect<StateProps, DispProps, RecomposeProps>(mapStateToProps, mapDispatchToProps),
)(RawReportsPage);



// WEBPACK FOOTER //
// ./src/ui/pages/reports/ReportsPage.tsx